/**
 * RelocationPlanPage — relocation planner computed from REAL household data
 *
 * Data: GET /api/jurisdictions/relocation-plan
 *   * per-destination drawdown tax at the household's £220k gross retirement
 *     target, IHT from the actual estate values, 20-year prize vs staying UK
 *   * Greece + Italy special-regime maths (7% pension regimes, non-dom /
 *     new-resident ANNUAL flat taxes) computed server-side
 *   * UK-exit rules (UK-source income, pre-departure CGT, 5-year rule)
 *
 * The Macau action-plan checklist below is editorial content (a real to-do
 * list), but every £ figure on this page comes from the API. NOT tax advice.
 */
import { useEffect, useState } from 'react'
import {
  Compass, CheckCircle2, AlertTriangle, Clock, Flag, RefreshCw, Landmark,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

// ── API types (match backend/api/jurisdictions.py::relocation_plan) ─────────

interface DestinationRow {
  jurisdiction_id: string
  name: string
  flag: string
  annual_drawdown_tax_gbp: number | null
  annual_drawdown_net_gbp: number | null
  annual_drawdown_effective_rate_pct: number | null
  iht_today_gbp: number | null
  iht_2027_gbp: number | null
  annual_saving_vs_uk_gbp: number | null
  decade_saving_vs_uk_gbp: number | null
  total_20yr_prize_gbp: number | null
  iht_saving_vs_uk_gbp: number | null
  notes: string | null
}

interface SpecialRegime {
  id: string
  jurisdiction_id: string
  flag: string
  name: string
  cost_basis: 'flat_annual' | 'pct_of_income'
  annual_cost_gbp: number | null
  annual_saving_vs_uk_gbp: number | null
  duration_years: number | null
  available_to_new_applicants: boolean
  annual_note: string | null
  conditions: string[]
  covers: string[]
  not_covered: string[]
}

interface UkExitRule {
  title: string
  detail: string
}

interface RelocationPlanResponse {
  caveat: string
  target_income_gbp: number
  joint: boolean
  eur_per_gbp_planning_rate: number
  uk_baseline: {
    annual_tax_gbp: number | null
    effective_rate_pct: number | null
    iht_today_gbp: number | null
    iht_2027_gbp: number | null
  } | null
  destinations: DestinationRow[]
  special_regimes: SpecialRegime[]
  uk_exit_rules: UkExitRule[]
  not_advice: string
}

// ── Macau action plan (editorial checklist — no £ claims live here) ─────────

interface ChecklistItem {
  text: string
  note?: string
}

interface Phase {
  number: number
  title: string
  duration: string
  colour: string
  bgColour: string
  items: ChecklistItem[]
  estimatedCost: string
}

const PHASES: Phase[] = [
  {
    number: 1,
    title: 'Preparation',
    duration: 'Months 1–6',
    colour: 'text-blue-light border-blue/30',
    bgColour: 'bg-blue/5',
    items: [
      { text: 'Unman renews Macau ID / passport if expired', note: 'Automatic — she holds citizenship' },
      { text: 'Engage international tax lawyer with UK↔HK/Macau experience', note: 'Recommended: Mishcon de Reya, Charles Russell Speechlys, Withers' },
      { text: 'Determine Simon\'s residency route', note: 'Spouse-of-citizen (most likely) vs investor visa vs employment' },
      { text: 'Map Simon\'s UK domicile status and deemed-domicile risk if returning later' },
      { text: 'Get IHT baseline valuation of all UK assets', note: 'Snapshot for future HMRC questions' },
      { text: 'List all UK bank accounts, brokers and pensions — decide what stays vs moves' },
      { text: 'Review AJ Bell SIPP policy on non-resident drawdown' },
      { text: 'Understand split-year treatment and when UK tax residency ends' },
    ],
    estimatedCost: '£5,000–£15,000 (legal fees, valuations)',
  },
  {
    number: 2,
    title: 'Establish Macau Residency',
    duration: 'Months 6–9',
    colour: 'text-emerald-light border-emerald/30',
    bgColour: 'bg-emerald/5',
    items: [
      { text: 'Unman establishes Macau primary residency', note: 'Rental lease, bank account, ID update at DSI (Identification Services Bureau)' },
      { text: 'Simon applies for spouse-dependent residence permit at DSAL / DSI' },
      { text: 'Open Macau bank account (requires physical presence)', note: 'Bank of China Macau or Tai Fung Bank are commonly used' },
      { text: 'Set up private health insurance', note: 'UK NHS not accessible as non-resident' },
      { text: 'Establish 90+ day physical presence in Macau to anchor residency for tax year' },
      { text: 'Register at the British Consulate (FCDO) as an overseas resident — not legally required but sensible' },
    ],
    estimatedCost: '£3,000–£8,000 (travel, relocation, bank setup, insurance)',
  },
  {
    number: 3,
    title: 'UK Tax Exit',
    duration: 'Months 9–12',
    colour: 'text-amber border-amber/30',
    bgColour: 'bg-amber/5',
    items: [
      { text: 'File P85 with HMRC on departure', note: 'Triggers split-year treatment if applicable; tells HMRC you\'ve left' },
      { text: 'Sell 35 Farm Avenue BEFORE departure', note: 'Crystallise UK CGT while still resident; non-resident CGT rules on UK property are less favourable' },
      { text: 'Decide on The Gables: keep as UK-sited asset (still subject to UK IHT on the property itself), let, or sell' },
      { text: 'Transfer liquid savings to Macau / HK accounts', note: 'Structure carefully with legal advice' },
      { text: 'Close or freeze UK ISAs', note: 'Non-residents cannot contribute; existing investments stay sheltered but no new money in' },
      { text: 'Confirm whether SIPP drawdown can be paid gross', note: 'Macau has NO UK DTA — expect UK tax at source to CONTINUE. This is the single biggest caveat of the Macau route.' },
      { text: 'Notify all UK brokers and AJ Bell of new non-resident status' },
      { text: 'Update HMRC self-assessment address and residency status' },
    ],
    estimatedCost: '£2,000–£5,000 (legal, accountancy, P85 filing)',
  },
  {
    number: 4,
    title: 'First Drawdown Year',
    duration: 'Months 12–18',
    colour: 'text-emerald-light border-emerald/30',
    bgColour: 'bg-emerald/5',
    items: [
      { text: 'Begin flexible SIPP drawdown from Macau residency', note: 'Without a UK DTA the drawdown remains UK-taxed at source — plan on UK rates, not 0%' },
      { text: 'File first Macau tax return if required', note: 'Most retirees without Macau-source income have nil local liability; confirm with local adviser' },
      { text: 'Monitor UK tax residency day count — do NOT exceed 90 days in UK', note: 'Automatic UK resident test kicks in at 91 days; 46 days at risk under ties test' },
      { text: 'Confirm all four UK ties have been cut: family tie, accommodation tie, work tie, 90-day tie' },
      { text: 'Set up quarterly review cadence with international tax adviser' },
      { text: 'Review investment platform access', note: 'Some UK brokers restrict non-resident accounts' },
    ],
    estimatedCost: '£3,000–£6,000/yr (ongoing tax compliance, adviser fees)',
  },
  {
    number: 5,
    title: 'Long-term Maintenance',
    duration: 'Ongoing',
    colour: 'text-text-secondary border-border',
    bgColour: 'bg-surface-card/30',
    items: [
      { text: 'Annual tax compliance: Macau (nil or minimal local) + UK non-resident return for remaining UK income' },
      { text: 'Review SIPP drawdown strategy annually against jurisdiction rules' },
      { text: 'Consider Portuguese citizenship optionality (Unman already holds)', note: 'EU optionality for healthcare, property, travel — useful backstop' },
      { text: 'Monitor UK Budget changes', note: 'April 2027 SIPP IHT rule already modelled in; watch for further residence/domicile changes' },
      { text: '5-year review: is the jurisdiction still right? Has Simon\'s domicile status changed?' },
      { text: 'Track the temporary-non-residence 5-year clock and long-term IHT residence tail' },
    ],
    estimatedCost: '£2,000–£4,000/yr (ongoing adviser + compliance)',
  },
]

const CRITICAL_RISKS = [
  {
    label: 'No UK-Macau DTA',
    text: 'Macau has no double tax agreement with the UK, so SIPP drawdown remains UK-taxed at source. The comparison table reflects this honestly — Macau\'s drawdown saving is £0/yr. Its value is IHT elimination and Unman\'s automatic residency.',
  },
  {
    label: 'Long-term UK residence (IHT tail)',
    text: 'Since April 2025 UK IHT follows long-term RESIDENCE: after leaving you can remain in scope for up to 10 years. Dying in year 3 abroad may still trigger full UK IHT on worldwide assets.',
  },
  {
    label: 'Non-resident CGT on UK property',
    text: 'UK land and property stays within UK CGT no matter where you live. Selling Farm Avenue before departure is the clean move.',
  },
  {
    label: 'Temporary non-residence',
    text: 'Return to UK residence within 5 years and gains realised while away are taxed in the year of return as if you never left.',
  },
  {
    label: 'UK accommodation tie',
    text: 'If The Gables remains available for your use, HMRC may treat you as having an accommodation tie — one of the factors that can make you UK-resident even below the 90-day threshold.',
  },
]

// ── Formatters ──────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) => {
  const v = n ?? 0
  return `${v < 0 ? '−' : ''}£${Math.abs(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

// ── Component ───────────────────────────────────────────────────────────────

export default function RelocationPlanPage() {
  const [data, setData] = useState<RelocationPlanResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openPhases, setOpenPhases] = useState<Set<number>>(new Set([1]))

  const fetchPlan = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<RelocationPlanResponse>('/api/jurisdictions/relocation-plan')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load relocation plan')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPlan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const togglePhase = (n: number) => {
    setOpenPhases(prev => {
      const next = new Set(prev)
      if (next.has(n)) next.delete(n)
      else next.add(n)
      return next
    })
  }

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in" aria-busy="true">
        <div className="h-8 w-64 skeleton" />
        <div className="h-24 skeleton" />
        <div className="h-64 skeleton" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light text-sm">{error ?? 'No data'}</p>
        <button type="button" onClick={fetchPlan} className="btn-secondary mt-3 gap-2 text-xs">
          <RefreshCw size={13} aria-hidden /> Retry
        </button>
      </div>
    )
  }

  const destinations = [...(data.destinations ?? [])].sort(
    (a, b) => (b.total_20yr_prize_gbp ?? 0) - (a.total_20yr_prize_gbp ?? 0),
  )
  const macau = destinations.find(d => d.jurisdiction_id === 'MO')
  const ukBaseline = data.uk_baseline

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Compass size={20} className="text-blue-light" aria-hidden />
            Relocation Planner
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Every figure computed from your household data at the {fmt(data.target_income_gbp)}/yr
            gross retirement target{data.joint ? ' (joint)' : ''}
          </p>
        </div>
        <button type="button" onClick={fetchPlan} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {/* ── THE caveat — once, prominent ── */}
      <div className="card p-4 border-2 border-rose/40 bg-rose/5 flex items-start gap-3" role="alert">
        <AlertTriangle size={18} className="text-rose-light shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="text-sm text-rose-light font-semibold">{data.caveat}</p>
          <p className="text-xs text-text-secondary mt-1">{data.not_advice}</p>
        </div>
      </div>

      {/* ── UK baseline ── */}
      {ukBaseline && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Landmark size={15} className="text-blue-light" aria-hidden />
            Staying UK-resident — the baseline
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 stagger-children">
            <div className="stat-card">
              <p className="stat-label">Annual drawdown tax</p>
              <p className="stat-value text-rose-light">{fmt(ukBaseline.annual_tax_gbp)}</p>
              <p className="text-xs text-text-muted">
                {(ukBaseline.effective_rate_pct ?? 0).toFixed(1)}% effective on {fmt(data.target_income_gbp)} gross
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">IHT today</p>
              <p className="stat-value text-amber">{fmt(ukBaseline.iht_today_gbp)}</p>
              <p className="text-xs text-text-muted">SIPP outside estate (current rules)</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">IHT from April 2027</p>
              <p className="stat-value text-rose-light">{fmt(ukBaseline.iht_2027_gbp)}</p>
              <p className="text-xs text-text-muted">SIPP inside estate — your actual values</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Macau honest headline ── */}
      {macau && (
        <div className="card p-5 border-2 border-emerald/40 bg-emerald/5">
          <div className="flex items-start gap-3 mb-4">
            <Flag size={18} className="text-emerald shrink-0 mt-0.5" aria-hidden />
            <div>
              <h2 className="panel-title">Macau — the honest numbers</h2>
              <p className="text-xs text-text-muted">
                Unman's citizenship makes it the easiest move — but there is NO UK DTA
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="stat-card">
              <p className="stat-label">Drawdown tax saving</p>
              <p className={clsx('stat-value', (macau.annual_saving_vs_uk_gbp ?? 0) > 0 ? 'text-emerald-light' : 'text-rose-light')}>
                {fmt(macau.annual_saving_vs_uk_gbp)}/yr
              </p>
              <p className="text-xs text-text-muted">
                No UK DTA → SIPP drawdown stays UK-taxed at source
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">IHT saving (one-time)</p>
              <p className="stat-value text-emerald-light">{fmt(macau.iht_saving_vs_uk_gbp)}</p>
              <p className="text-xs text-text-muted">vs UK IHT from April 2027, your actual estate</p>
            </div>
            <div className="stat-card border-emerald/40">
              <p className="stat-label">Total 20-year prize</p>
              <p className="stat-hero text-emerald">{fmt(macau.total_20yr_prize_gbp)}</p>
              <p className="text-xs text-text-muted">Drawdown savings × 20 + IHT saving</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Destination comparison table ── */}
      <section className="card p-5">
        <h2 className="panel-title mb-1">
          All destinations vs staying UK — at {fmt(data.target_income_gbp)}/yr gross
        </h2>
        <p className="text-xs text-text-muted mb-3">
          Drawdown tax from each regime's published rates; IHT from your actual estate values;
          sorted by total 20-year prize. Negative savings mean it costs MORE than staying.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs table-pro">
            <thead>
              <tr>
                <th>Destination</th>
                <th className="!text-right">Tax/yr</th>
                <th className="!text-right">Eff. rate</th>
                <th className="!text-right">Saving/yr vs UK</th>
                <th className="!text-right">IHT 2027</th>
                <th className="!text-right">20-yr prize</th>
              </tr>
            </thead>
            <tbody>
              {destinations.map(d => {
                const isUk = d.jurisdiction_id === 'UK'
                const saving = d.annual_saving_vs_uk_gbp ?? 0
                return (
                  <tr
                    key={d.jurisdiction_id}
                    className={clsx(isUk && 'bg-blue/5')}
                    title={d.notes ?? undefined}
                  >
                    <td className="py-2 px-2 text-text-primary font-medium whitespace-nowrap">
                      <span aria-hidden className="mr-1.5">{d.flag}</span>
                      {d.name}
                      {isUk && <span className="badge badge-blue text-[9px] ml-1.5">baseline</span>}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-text-primary">{fmt(d.annual_drawdown_tax_gbp)}</td>
                    <td className="py-2 px-2 text-right font-mono text-text-muted">
                      {(d.annual_drawdown_effective_rate_pct ?? 0).toFixed(1)}%
                    </td>
                    <td className={clsx(
                      'py-2 px-2 text-right font-mono font-bold',
                      saving > 0 ? 'text-emerald-light' : saving < 0 ? 'text-rose-light' : 'text-text-muted',
                    )}>
                      {isUk ? '—' : fmt(saving)}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-text-muted">{fmt(d.iht_2027_gbp)}</td>
                    <td className="py-2 px-2 text-right font-mono text-emerald-light font-bold">
                      {isUk ? '—' : fmt(d.total_20yr_prize_gbp)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Greece + Italy special regimes ── */}
      <section className="space-y-3">
        <h2 className="panel-title">
          Greece &amp; Italy — the special regimes, priced at your target
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(data.special_regimes ?? []).map(r => {
            const saving = r.annual_saving_vs_uk_gbp ?? 0
            const positive = saving > 0
            return (
              <div
                key={r.id}
                className={clsx(
                  'card p-5 border-2',
                  !r.available_to_new_applicants
                    ? 'border-border/40 opacity-75'
                    : positive
                      ? 'border-emerald/30'
                      : 'border-amber/30',
                )}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <h3 className="text-sm font-bold text-text-primary">
                    <span aria-hidden className="mr-1.5">{r.flag}</span>
                    {r.name}
                  </h3>
                  <div className="flex gap-1.5 items-center">
                    {!r.available_to_new_applicants && (
                      <span className="badge badge-red text-[9px]">Closed to new applicants</span>
                    )}
                    {r.cost_basis === 'flat_annual' && (
                      <span className="badge badge-amber text-[9px]">ANNUAL charge — every year</span>
                    )}
                    <span className="badge badge-blue text-[9px]">{r.duration_years ?? '—'} yrs max</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="stat-card">
                    <p className="stat-label">Cost per year</p>
                    <p className="stat-value text-text-primary">{fmt(r.annual_cost_gbp)}</p>
                    <p className="text-[10px] text-text-muted">
                      {r.cost_basis === 'flat_annual'
                        ? `flat, every year (EUR at ${data.eur_per_gbp_planning_rate} planning rate)`
                        : `7% of your ${fmt(data.target_income_gbp)} target`}
                    </p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-label">vs staying UK</p>
                    <p className={clsx('stat-value', positive ? 'text-emerald-light' : 'text-rose-light')}>
                      {positive ? `saves ${fmt(saving)}/yr` : `costs ${fmt(Math.abs(saving))}/yr MORE`}
                    </p>
                    <p className="text-[10px] text-text-muted">at your income level — honest maths</p>
                  </div>
                </div>

                {r.annual_note && (
                  <p className="text-xs text-text-secondary mb-3 leading-relaxed">{r.annual_note}</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="font-semibold text-text-primary mb-1">Qualifying conditions</p>
                    <ul className="space-y-1 text-text-secondary">
                      {r.conditions.map((c, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <CheckCircle2 size={11} className="text-text-muted shrink-0 mt-0.5" aria-hidden />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="font-semibold text-emerald-light mb-1">Sheltered</p>
                      <ul className="space-y-0.5 text-text-secondary">
                        {r.covers.map((c, i) => <li key={i}>· {c}</li>)}
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-amber mb-1">NOT sheltered</p>
                      <ul className="space-y-0.5 text-text-secondary">
                        {r.not_covered.map((c, i) => <li key={i}>· {c}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* ── UK exit rules — apply to EVERY destination ── */}
      <section className="card p-5 border-2 border-amber/40 bg-amber/5">
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <AlertTriangle size={15} className="text-amber" aria-hidden />
          UK exit rules — these apply wherever you go
        </h2>
        <ul className="space-y-3">
          {(data.uk_exit_rules ?? []).map((rule, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              <AlertTriangle size={14} className="text-amber shrink-0 mt-0.5" aria-hidden />
              <div>
                <span className="font-semibold text-amber">{rule.title}: </span>
                <span className="text-text-secondary text-xs">{rule.detail}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Macau migration phases (editorial checklist) ── */}
      <div className="space-y-3">
        <h2 className="panel-title">
          Macau Migration Phases — the action checklist
        </h2>
        {PHASES.map(phase => {
          const isOpen = openPhases.has(phase.number)
          return (
            <div
              key={phase.number}
              className={clsx('card border-2 overflow-hidden', phase.colour.split(' ')[1])}
            >
              <button
                type="button"
                onClick={() => togglePhase(phase.number)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-surface-hover/40 transition-colors focus-visible:ring-2 focus-visible:ring-blue rounded-t-xl"
                aria-expanded={isOpen}
              >
                <span className={clsx(
                  'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0',
                  phase.bgColour,
                  phase.colour.split(' ')[0],
                  'border',
                  phase.colour.split(' ')[1],
                )}>
                  {phase.number}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-text-primary">
                      Phase {phase.number} — {phase.title}
                    </span>
                    <span className={clsx(
                      'inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border',
                      phase.bgColour,
                      phase.colour,
                    )}>
                      <Clock size={9} aria-hidden />
                      {phase.duration}
                    </span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">
                    {phase.items.length} action items · {phase.estimatedCost}
                  </p>
                </div>
                <span className="text-text-muted text-xs shrink-0 select-none">
                  {isOpen ? '▲' : '▼'}
                </span>
              </button>

              {isOpen && (
                <div className={clsx('border-t border-border px-4 pb-4 pt-3', phase.bgColour)}>
                  <ul className="space-y-2.5">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2
                          size={15}
                          className="text-text-muted shrink-0 mt-0.5"
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <span className="text-text-primary">{item.text}</span>
                          {item.note && (
                            <p className="text-xs text-text-muted mt-0.5">{item.note}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-3 border-t border-border/60">
                    <p className="text-xs text-text-muted">
                      <span className="text-text-secondary font-semibold">Estimated cost: </span>
                      {phase.estimatedCost}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* ── Critical risks ── */}
      <div className="card p-5 border-2 border-amber/40 bg-amber/5">
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <AlertTriangle size={15} className="text-amber" aria-hidden />
          Critical Risks — Read Before Acting
        </h2>
        <ul className="space-y-3">
          {CRITICAL_RISKS.map((risk, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              <AlertTriangle size={14} className="text-amber shrink-0 mt-0.5" aria-hidden />
              <div>
                <span className="font-semibold text-amber">{risk.label}: </span>
                <span className="text-text-secondary">{risk.text}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Disclaimer footer ── */}
      <div className="card p-4 text-xs text-text-muted border-border/40">
        <p>
          <span className="font-semibold text-text-secondary">Disclaimer: </span>
          Figures are computed live from your household data and published regime rules
          (rules current to early 2026; EUR converted at a {data.eur_per_gbp_planning_rate} EUR/GBP
          long-range planning rate). This is a planning framework, NOT professional tax or legal
          advice. Tax law changes frequently and individual circumstances vary. Consult a
          qualified cross-border tax adviser before taking any action.
        </p>
      </div>

    </div>
  )
}
